// src/admin/pages/AdminLogin.jsx
// Deliberately plain compared to the merchant Login.jsx (no marketing
// panel, no Instagram OAuth) -- this is an internal tool, not a page a
// merchant or visitor will ever land on. Simple email input -> magic link.
//
// Turnstile widget added on top of the original version -- same widget,
// same script-loading helper, and same request/response shape as
// TeamLogin.jsx's RequestLink component, since this endpoint is exposed
// the same way (public POST, no auth) and needs the same protection
// against being hammered.

import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import adminApi from "@/admin/lib/adminApi";

const TURNSTILE_SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY;

function loadTurnstile() {
  return new Promise((resolve) => {
    if (window.turnstile) return resolve(true);
    const existing = document.querySelector('script[src*="challenges.cloudflare.com/turnstile"]');
    if (existing) {
      existing.addEventListener("load", () => resolve(true));
      existing.addEventListener("error", () => resolve(false));
      return;
    }
    const s = document.createElement("script");
    s.src = "https://challenges.cloudflare.com/turnstile/v0/api.js";
    s.async = true;
    s.defer = true;
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });
}

export default function AdminLogin() {
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [err, setErr] = useState("");

  const [captchaToken, setCaptchaToken] = useState("");
  const [captchaReady, setCaptchaReady] = useState(false);
  const widgetRef = useRef(null);
  const widgetIdRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    loadTurnstile().then((ok) => {
      if (cancelled || !ok || !widgetRef.current || !window.turnstile) return;
      widgetIdRef.current = window.turnstile.render(widgetRef.current, {
        sitekey: TURNSTILE_SITE_KEY,
        callback: (token) => setCaptchaToken(token),
        "expired-callback": () => setCaptchaToken(""),
        "error-callback": () => setCaptchaToken(""),
      });
      setCaptchaReady(true);
    });
    return () => {
      cancelled = true;
      if (widgetIdRef.current && window.turnstile) {
        window.turnstile.remove(widgetIdRef.current);
      }
    };
  }, []);

  async function handleSubmit(e) {
    e.preventDefault();
    if (!email.trim()) return;
    if (!captchaToken) {
      setErr("Please complete the verification check.");
      return;
    }
    setErr("");
    setSubmitting(true);
    try {
      await adminApi.post("/admin/login-request", {
        email: email.trim(),
        turnstile_token: captchaToken,
      });
      // Always show the same "check your email" state regardless of
      // whether the address matched an admin account -- the backend
      // deliberately doesn't reveal that (see login-request workflow),
      // so the frontend shouldn't either.
      setSent(true);
    } catch (e) {
      const code = e?.response?.data?.error;
      if (code === "captcha_failed") {
        setErr("Verification failed — please try the check again.");
        if (widgetIdRef.current && window.turnstile) {
          window.turnstile.reset(widgetIdRef.current);
        }
        setCaptchaToken("");
      } else {
        toast.error("Something went wrong. Please try again.");
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="grid min-h-screen place-items-center bg-cream p-6">
      <div
        className="w-full max-w-sm rounded-[2rem] border border-ink/10 bg-white p-8 shadow-sm"
        data-testid="admin-login-card"
      >
        <p className="text-xs font-bold uppercase tracking-[0.25em] text-hotpink">
          CheckoutDM Admin
        </p>

        {sent ? (
          <div className="mt-4">
            <h2 className="font-display text-2xl tracking-tighter">
              Check your email
            </h2>
            <p className="mt-2 text-sm text-ink/60">
              If that email belongs to an admin account, a login link is on
              its way. It expires in 15 minutes and works once.
            </p>
            <button
              type="button"
              className="mt-6 text-sm font-semibold text-hotpink underline"
              onClick={() => setSent(false)}
            >
              Use a different email
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="mt-4">
            <h2 className="font-display text-2xl tracking-tighter">
              Admin sign-in
            </h2>
            <p className="mt-2 text-sm text-ink/60">
              Enter your admin email. We'll send you a one-time login link.
            </p>

            <label className="mt-5 block text-xs font-bold uppercase tracking-wide text-ink/50">
              Email
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="admin@checkoutdm.com"
              className="mt-1.5 w-full rounded-xl border border-ink/15 px-4 py-2.5 text-sm outline-none focus:border-hotpink"
              data-testid="admin-login-email"
              required
            />

            {err && <p className="mt-3 text-xs font-semibold text-hotpink">{err}</p>}

            <div ref={widgetRef} className="mt-4" data-testid="admin-login-captcha" />

            <button
              type="submit"
              disabled={submitting || !captchaReady || !captchaToken}
              className="mt-5 w-full rounded-xl bg-hotpink px-4 py-2.5 text-sm font-semibold text-white disabled:opacity-60"
              data-testid="admin-login-submit"
            >
              {submitting ? "Sending..." : "Send login link"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
